/*
 * Using mui-rff
 * Check following link for more info: https://github.com/lookfirst/mui-rff
 * */

import React from 'react';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import * as Yup from 'yup';
import { makeRequired, makeValidate } from 'mui-rff';
import { Form } from 'react-final-form';
import Alert from '@material-ui/lab/Alert/Alert';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import { useTranslation } from 'react-i18next';

import Link from '@material-ui/core/Link';
import FormRow from '../../../components/ui/FormRow';
import FormTextField from '../../../components/ui/FormTextField';

const useStyles = makeStyles((theme) => ({
  form: {
    width: '100%', // Fix IE 11 issue.
    marginTop: theme.spacing(2),
  },
  grid: {
    width: '100%',
    margin: 0,
  },
  submitBtnWrapper: {
    position: 'relative',
  },
  submitBtn: {
    margin: theme.spacing(2, 0, 1),
    height: 50,
  },
  submitBtnProgress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
}));

interface Props {
  userEmail: string;
  loading: boolean;
  errorTxt: string | undefined;
  onSubmit: (data: ForgotPasswordFormActivateData) => void;
  onAlertCloseClick: () => void;
  onResendActivationCodeClick: () => void;
}

export interface ForgotPasswordFormActivateData {
  code: number | undefined;
}

const formSchema = Yup.object().shape({
  code: Yup.number().required('Code is a required field.'),
});

const validate = makeValidate(formSchema);
const required = makeRequired(formSchema);

const ForgotPasswordFormActivate: React.FC<Props> = ({
  userEmail,
  loading,
  errorTxt,
  onSubmit,
  onAlertCloseClick,
  onResendActivationCodeClick,
}: Props) => {
  const classes = useStyles();
  const [t] = useTranslation();
  const subscription = { submitting: true };
  const handleSubmitClick = (values: ForgotPasswordFormActivateData) => {
    onSubmit(values);
  };
  const handleResendActivationCodeClick = () => {
    onResendActivationCodeClick();
  };
  const handleAlertCloseClick = () => {
    onAlertCloseClick();
  };

  return (
    <Form
      key={subscription as any}
      subscription={subscription}
      validate={validate}
      onSubmit={handleSubmitClick}
      render={({ handleSubmit, submitting, pristine, invalid }) => (
        <form className={classes.form} onSubmit={handleSubmit} noValidate={true} autoComplete="new-password">
          <Grid className={classes.grid} container direction="column" spacing={2}>
            <Grid item xs>
              <Typography
                variant="body1"
                gutterBottom
                dangerouslySetInnerHTML={{
                  __html: t('pages.forgot_password.step_activate_title', { userEmail: `<b>${userEmail}</b>` }),
                }}
              ></Typography>
            </Grid>
            <Grid item xs>
              <FormRow label={t('pages.forgot_password.step_activate_field_label')}>
                <FormTextField
                  id="code"
                  name="code"
                  type="number"
                  required={required.code}
                  fullWidth
                  autoFocus
                  helperText={t('pages.forgot_password.step_activate_helper_text')}
                />
              </FormRow>

              {/* Error alert */}
              {errorTxt && (
                <Alert variant="outlined" onClose={handleAlertCloseClick} severity="error">
                  {errorTxt}
                </Alert>
              )}

              {/* Submit */}
              <div className={classes.submitBtnWrapper}>
                <Button
                  className={classes.submitBtn}
                  type="submit"
                  disabled={submitting || pristine || invalid || loading}
                  fullWidth
                  variant="contained"
                  color="primary"
                >
                  {t('global.submit_button')}
                </Button>
                {loading && <CircularProgress className={classes.submitBtnProgress} size={24} />}
              </div>

              {/* Aux. buttons */}
              <Grid container>
                <Grid item xs>
                  <Link href="#" variant="body2" onClick={handleResendActivationCodeClick}>
                    {t('pages.forgot_password.step_activate_resend_link')}
                  </Link>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </form>
      )}
    />
  );
};

export default ForgotPasswordFormActivate;
